/**
 * Prompt Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatResponsiveCornerRadiusFormat,
  formatResponsiveValue,
  formatSpacingFormat,
  formatTextAlignmentFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseColorFormat,
  parseSpacingFormat,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  GradientDirection,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  TypographyValue,
} from '../../types'
import promptTemplate from './prompt.template.json'

export interface IPromptComponentSettings {
  // Data settings
  'prompt-data-source': string
  'prompt-data-title': string
  'prompt-data-description': string
  'prompt-data-sort-type': string

  // Design settings
  'prompt-design-background-color': ColorAdaptiveValue<string>
  'prompt-design-background-color-solid': ColorAdaptiveValue<ColorVariant>
  'prompt-design-background-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-background-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-background-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'prompt-design-scheme': any
  'prompt-design-button-color-variant': ColorAdaptiveValue<string>
  'prompt-design-button-color-variant-solid': ColorAdaptiveValue<ColorVariant>
  'prompt-design-button-color-variant-start-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-button-color-variant-end-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-button-color-variant-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'prompt-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'prompt-design-h-align': ResponsiveValue<TextAlignment | 'parent'>
  'prompt-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'prompt-design-chip-color': ColorAdaptiveValue<string>
  'prompt-design-chip-color-solid': ColorAdaptiveValue<ColorVariant>
  'prompt-design-chip-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-chip-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-chip-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'prompt-design-chips-alignment': ResponsiveValue<TextAlignment>
  'prompt-design-chips-gap': number[]
  'prompt-design-placeholder-color': ColorAdaptiveValue<string>
  'prompt-design-placeholder-color-solid': ColorAdaptiveValue<ColorVariant>
  'prompt-design-placeholder-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-placeholder-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'prompt-design-placeholder-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'prompt-design-typography': {
    'prompt-design-placeholder-typography': TypographyValue
  }
  'prompt-design-border-thickness': number
  'prompt-design-border-color': string
  'prompt-design-border-color-solid': string
  'prompt-design-elevation': boolean
  'prompt-design-corner-radius': ResponsiveValue<string>
  'prompt-design-button-corner-radius': string
  'prompt-design-number-default-items': ResponsiveValue<number>

  // Actions
  'prompt-action-on-button-click': BuilderActions
  'prompt-action-on-improve-ai-click': BuilderActions

  // Behavior settings
  'prompt-behavior-accepted-files': string[]
  'prompt-behavior-color-selection': boolean
  'prompt-behavior-file-selection': boolean
  'prompt-behavior-font-selection': boolean
  'prompt-behavior-max-files': number[]
  'prompt-behavior-max-size': {
    unit: string
    value: number
  }
}

export interface IPromptComponent extends IBuilderComponent {
  componentId: 'prompt'
  groupId: 'app'
  settings: IPromptComponentSettings
}

/**
 * Prompt AI Input
 */
export interface AiPromptInput {
  dataSource: string // Table ID
  titleColumn?: string // Column ID for title
  descriptionColumn?: string // Column ID for description
  sortType?: 'asc' | 'desc' // Sort direction
  width?: string // Format: "w:100%"
  alignment?: string // Format: "parent" or "center"
  margin?: string // Format: "m:24px"
  backgroundColor?: string // Format: "solid:primary-40" or "adaptive"
  buttonColor?: string // Format: "solid:primary-40" or "adaptive"
  chipColor?: string // Format: "solid:primary-40" or "adaptive"
  placeholderColor?: string // Format: "solid:primary-40" or "adaptive"
  borderColor?: string // Border color
  borderThickness?: number // Border thickness
  cornerRadius?: string // Format: "none", "small", "medium", "large"
  buttonCornerRadius?: string // Button corner radius
  chipsAlignment?: string // Format: "center" or "left"
  chipsGap?: number // Gap between chips
  elevation?: boolean // Shadow elevation
  typography?: {
    placeholder?: string // Placeholder typography
  }
  numberDefaultItems?: number | string // Format: "4" or "4 ta:6 de:8"
  acceptedFiles?: string[] // Accepted file extensions
  colorSelection?: boolean // Enable color selection
  fileSelection?: boolean // Enable file selection
  fontSelection?: boolean // Enable font selection
  maxFiles?: number // Maximum files
  maxSize?: {
    unit: string
    value: number
  }
  actionOnButtonClick?: BuilderActions
  actionOnImproveAiClick?: BuilderActions
}

const PROMPT_DEFAULT_TEMPLATE = promptTemplate as unknown as IPromptComponentSettings

export class PromptComp {
  getTemplate(): IPromptComponentSettings {
    return PROMPT_DEFAULT_TEMPLATE
  }

  /**
   * format prompt component settings to AI prompt input
   */
  format(settings: IPromptComponentSettings): AiPromptInput {
    const result: AiPromptInput = {
      dataSource: '',
    }

    // Data - Source
    if (settings['prompt-data-source']) {
      result.dataSource = settings['prompt-data-source']
    }

    // Data - Columns
    if (settings['prompt-data-title']) {
      result.titleColumn = settings['prompt-data-title']
    }

    if (settings['prompt-data-description']) {
      result.descriptionColumn = settings['prompt-data-description']
    }

    // Data - Sort
    if (settings['prompt-data-sort-type']) {
      result.sortType = settings['prompt-data-sort-type'] as 'asc' | 'desc'
    }

    // Design - Width (handle OverflowSettings)
    if (settings['prompt-design-width']) {
      const width = formatWidthFormat(settings['prompt-design-width'])
      if (width) {
        result.width = width
      }
    }

    // Design - Alignment
    if (settings['prompt-design-h-align']) {
      result.alignment =
        formatTextAlignmentFormat(settings['prompt-design-h-align'] as any)?.replace(/^t:/, '') || undefined
    }

    // Design - Margin (handle OverflowSettings)
    if (settings['prompt-design-margin']) {
      const margin = formatSpacingFormat(settings['prompt-design-margin'])
      if (margin) {
        result.margin = margin
      }
    }

    // Design - Background Color
    if (settings['prompt-design-background-color']) {
      result.backgroundColor = formatColorFormat({
        colorType: settings['prompt-design-background-color'] as any,
        colorSolid: settings['prompt-design-background-color-solid'],
        colorStartGradient: settings['prompt-design-background-color-start-gradient'],
        colorEndGradient: settings['prompt-design-background-color-end-gradient'],
        colorGradientDirection: settings['prompt-design-background-color-gradient-direction'],
      })
    }

    // Design - Button Color
    if (settings['prompt-design-button-color-variant']) {
      result.buttonColor = formatColorFormat({
        colorType: settings['prompt-design-button-color-variant'] as any,
        colorSolid: settings['prompt-design-button-color-variant-solid'],
        colorStartGradient: settings['prompt-design-button-color-variant-start-gradient'],
        colorEndGradient: settings['prompt-design-button-color-variant-end-gradient'],
        colorGradientDirection: settings['prompt-design-button-color-variant-gradient-direction'],
      })
    }

    // Design - Chip Color
    if (settings['prompt-design-chip-color']) {
      result.chipColor = formatColorFormat({
        colorType: settings['prompt-design-chip-color'] as any,
        colorSolid: settings['prompt-design-chip-color-solid'],
        colorStartGradient: settings['prompt-design-chip-color-start-gradient'],
        colorEndGradient: settings['prompt-design-chip-color-end-gradient'],
        colorGradientDirection: settings['prompt-design-chip-color-gradient-direction'],
      })
    }

    // Design - Placeholder Color
    if (settings['prompt-design-placeholder-color']) {
      result.placeholderColor = formatColorFormat({
        colorType: settings['prompt-design-placeholder-color'] as any,
        colorSolid: settings['prompt-design-placeholder-color-solid'],
        colorStartGradient: settings['prompt-design-placeholder-color-start-gradient'],
        colorEndGradient: settings['prompt-design-placeholder-color-end-gradient'],
        colorGradientDirection: settings['prompt-design-placeholder-color-gradient-direction'],
      })
    }

    // Design - Border
    if (settings['prompt-design-border-color']) {
      result.borderColor = settings['prompt-design-border-color']
    }

    if (settings['prompt-design-border-thickness'] !== undefined) {
      result.borderThickness = settings['prompt-design-border-thickness']
    }

    // Design - Corner Radius
    if (settings['prompt-design-corner-radius']) {
      result.cornerRadius = formatResponsiveCornerRadiusFormat(settings['prompt-design-corner-radius'])
    }

    if (settings['prompt-design-button-corner-radius']) {
      result.buttonCornerRadius = settings['prompt-design-button-corner-radius']
    }

    // Design - Chips Alignment
    if (settings['prompt-design-chips-alignment']) {
      result.chipsAlignment =
        formatTextAlignmentFormat(settings['prompt-design-chips-alignment'])?.replace(/^t:/, '') || undefined
    }

    if (settings['prompt-design-chips-gap'] && settings['prompt-design-chips-gap'].length > 0) {
      result.chipsGap = settings['prompt-design-chips-gap'][0]
    }

    // Design - Elevation
    if (settings['prompt-design-elevation'] !== undefined) {
      result.elevation = settings['prompt-design-elevation']
    }

    // Design - Typography
    if (settings['prompt-design-typography']) {
      result.typography = {}
      if (settings['prompt-design-typography']['prompt-design-placeholder-typography']) {
        result.typography.placeholder = formatTypographyFormat({
          phone: settings['prompt-design-typography']['prompt-design-placeholder-typography'],
        } as any)
      }
    }

    // Design - Number Default Items
    if (settings['prompt-design-number-default-items']) {
      const allSame = Object.values(settings['prompt-design-number-default-items']).every(
        (v) => v === Object.values(settings['prompt-design-number-default-items'])[0]
      )
      if (allSame) {
        result.numberDefaultItems = Object.values(settings['prompt-design-number-default-items'])[0]
      } else {
        result.numberDefaultItems = formatResponsiveValue(settings['prompt-design-number-default-items'], (val) =>
          String(val)
        )
      }
    }

    // Behavior
    if (settings['prompt-behavior-accepted-files']) {
      result.acceptedFiles = settings['prompt-behavior-accepted-files']
    }

    if (settings['prompt-behavior-color-selection'] !== undefined) {
      result.colorSelection = settings['prompt-behavior-color-selection']
    }

    if (settings['prompt-behavior-file-selection'] !== undefined) {
      result.fileSelection = settings['prompt-behavior-file-selection']
    }

    if (settings['prompt-behavior-font-selection'] !== undefined) {
      result.fontSelection = settings['prompt-behavior-font-selection']
    }

    if (settings['prompt-behavior-max-files'] && settings['prompt-behavior-max-files'].length > 0) {
      result.maxFiles = settings['prompt-behavior-max-files'][0]
    }

    if (settings['prompt-behavior-max-size']) {
      result.maxSize = settings['prompt-behavior-max-size']
    }

    return result
  }

  /**
   * parse AI prompt input to prompt component settings
   */
  parse(input: AiPromptInput): IPromptComponentSettings {
    const result = deepMerge(PROMPT_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['prompt-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.titleColumn) {
      result['prompt-data-title'] = input.titleColumn
    }

    if (input.descriptionColumn) {
      result['prompt-data-description'] = input.descriptionColumn
    }

    // Data - Sort
    if (input.sortType) {
      result['prompt-data-sort-type'] = input.sortType
    }

    // Design - Width
    if (input.width) {
      result['prompt-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentMap: Record<string, TextAlignment | 'parent'> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      const alignment = alignmentMap[input.alignment] || 'parent'
      result['prompt-design-h-align'] = {
        phone: alignment as TextAlignment,
        'phone-landscape': alignment as TextAlignment,
        tablet: alignment as TextAlignment,
        'tablet-landscape': alignment as TextAlignment,
        desktop: alignment as TextAlignment,
        tv: alignment as TextAlignment,
      }
    }

    // Design - Margin
    if (input.margin) {
      result['prompt-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Background Color
    if (input.backgroundColor) {
      const colorParsed = parseColorFormat(input.backgroundColor)
      result['prompt-design-background-color'] = colorParsed.colorType
      result['prompt-design-background-color-solid'] = colorParsed.colorSolid
      result['prompt-design-background-color-start-gradient'] = colorParsed.colorStartGradient
      result['prompt-design-background-color-end-gradient'] = colorParsed.colorEndGradient
      result['prompt-design-background-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Button Color
    if (input.buttonColor) {
      const colorParsed = parseColorFormat(input.buttonColor)
      result['prompt-design-button-color-variant'] = colorParsed.colorType
      result['prompt-design-button-color-variant-solid'] = colorParsed.colorSolid
      result['prompt-design-button-color-variant-start-gradient'] = colorParsed.colorStartGradient
      result['prompt-design-button-color-variant-end-gradient'] = colorParsed.colorEndGradient
      result['prompt-design-button-color-variant-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Chip Color
    if (input.chipColor) {
      const colorParsed = parseColorFormat(input.chipColor)
      result['prompt-design-chip-color'] = colorParsed.colorType
      result['prompt-design-chip-color-solid'] = colorParsed.colorSolid
      result['prompt-design-chip-color-start-gradient'] = colorParsed.colorStartGradient
      result['prompt-design-chip-color-end-gradient'] = colorParsed.colorEndGradient
      result['prompt-design-chip-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Placeholder Color
    if (input.placeholderColor) {
      const colorParsed = parseColorFormat(input.placeholderColor)
      result['prompt-design-placeholder-color'] = colorParsed.colorType
      result['prompt-design-placeholder-color-solid'] = colorParsed.colorSolid
      result['prompt-design-placeholder-color-start-gradient'] = colorParsed.colorStartGradient
      result['prompt-design-placeholder-color-end-gradient'] = colorParsed.colorEndGradient
      result['prompt-design-placeholder-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Border
    if (input.borderColor) {
      result['prompt-design-border-color'] = input.borderColor
    }

    if (input.borderThickness !== undefined) {
      result['prompt-design-border-thickness'] = input.borderThickness
    }

    if (input.borderColor) {
      const colorParsed = parseColorFormat(input.borderColor)
      result['prompt-design-border-color-solid'] = colorParsed.colorSolid.light
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      const radiusMap: Record<string, string> = {
        none: 'none',
        small: 'small',
        medium: 'medium',
        large: 'large',
      }
      const radius = radiusMap[input.cornerRadius] || 'medium'
      result['prompt-design-corner-radius'] = {
        phone: radius,
        'phone-landscape': radius,
        tablet: radius,
        'tablet-landscape': radius,
        desktop: radius,
        tv: radius,
      }
    }

    if (input.buttonCornerRadius) {
      result['prompt-design-button-corner-radius'] = input.buttonCornerRadius
    }

    // Design - Chips
    if (input.chipsAlignment) {
      const alignmentMap: Record<string, TextAlignment> = {
        left: 'left',
        center: 'center',
        right: 'right',
      }
      const alignment = alignmentMap[input.chipsAlignment] || 'center'
      result['prompt-design-chips-alignment'] = {
        phone: alignment,
        'phone-landscape': alignment,
        tablet: alignment,
        'tablet-landscape': alignment,
        desktop: alignment,
        tv: alignment,
      }
    }

    if (input.chipsGap !== undefined) {
      result['prompt-design-chips-gap'] = [input.chipsGap]
    }

    // Design - Elevation
    if (input.elevation !== undefined) {
      result['prompt-design-elevation'] = input.elevation
    }

    // Design - Typography
    if (input.typography?.placeholder) {
      const placeholderTypo = parseTypographyFormat(input.typography.placeholder)
      result['prompt-design-typography']['prompt-design-placeholder-typography'] = placeholderTypo.phone
    }

    // Design - Number Default Items
    if (input.numberDefaultItems !== undefined) {
      if (typeof input.numberDefaultItems === 'number') {
        result['prompt-design-number-default-items'] = {
          phone: input.numberDefaultItems,
          'phone-landscape': input.numberDefaultItems,
          tablet: input.numberDefaultItems,
          'tablet-landscape': input.numberDefaultItems,
          desktop: input.numberDefaultItems,
          tv: input.numberDefaultItems,
        }
      } else {
        // Parse responsive format
        const parts = input.numberDefaultItems.split(/\s+/)
        const itemsObj: ResponsiveValue<number> = {
          phone: 4,
          'phone-landscape': 4,
          tablet: 4,
          'tablet-landscape': 4,
          desktop: 4,
          tv: 4,
        }

        for (const part of parts) {
          if (part.includes(':')) {
            const [breakpoint, value] = part.split(':')
            const bpMap: Record<string, keyof ResponsiveValue<number>> = {
              p: 'phone',
              pl: 'phone-landscape',
              t: 'tablet',
              tl: 'tablet-landscape',
              de: 'desktop',
              tv: 'tv',
            }
            const bp = bpMap[breakpoint]
            if (bp) {
              itemsObj[bp] = parseInt(value) || 4
            }
          } else {
            const value = parseInt(input.numberDefaultItems) || 4
            Object.keys(itemsObj).forEach((key) => {
              itemsObj[key as keyof ResponsiveValue<number>] = value
            })
          }
        }

        result['prompt-design-number-default-items'] = itemsObj
      }
    }

    // Behavior
    if (input.acceptedFiles) {
      result['prompt-behavior-accepted-files'] = input.acceptedFiles
    }

    if (input.colorSelection !== undefined) {
      result['prompt-behavior-color-selection'] = input.colorSelection
    }

    if (input.fileSelection !== undefined) {
      result['prompt-behavior-file-selection'] = input.fileSelection
    }

    if (input.fontSelection !== undefined) {
      result['prompt-behavior-font-selection'] = input.fontSelection
    }

    if (input.maxFiles !== undefined) {
      result['prompt-behavior-max-files'] = [input.maxFiles]
    }

    if (input.maxSize) {
      result['prompt-behavior-max-size'] = input.maxSize
    }

    // Actions
    if (input.actionOnButtonClick) {
      result['prompt-action-on-button-click'] = input.actionOnButtonClick
    }

    if (input.actionOnImproveAiClick) {
      result['prompt-action-on-improve-ai-click'] = input.actionOnImproveAiClick
    }

    return result
  }
}
